import { useId, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import type { SimplesResumoResponse, TributosPorOrigemResponse } from '@triade/contracts';
import { formatarData, formatarDinheiro, formatarFracaoPercentual } from '@/lib/format';
import { somar } from '@/relatorios/numeros';
import type { BaseValoresDespesa } from './painel-preco';

/** Aviso do perfil tributario (mesmo formato de `tributosPorOrigem.avisos` e `avisosTributarios`). */
export type AvisoTributario = { codigo: string; severidade: string; tributo?: string; mensagem: string };

/** Substituicao: ICMS-ST configurada e ainda nao calculada pelo motor. */
const CODIGOS_SUBSTITUICAO = ['ST_NAO_CALCULADA'];
/** Tributo que a tabela deveria trazer e nao trouxe (DAS, IRPJ/CSLL do Real, UF de origem, ICMS fixo sem linha). */
const CODIGOS_NAO_CONFIGURADO = ['DAS_NAO_INFORMADA', 'IRPJ_AUSENTE', 'CSLL_AUSENTE', 'UF_ORIGEM_AUSENTE', 'ICMS_FIXO_SEM_LINHA'];

/** Texto de tela de avisos com codigo conhecido (o codigo e o texto de apresentacao; o restante segue a mensagem da API). */
const ROTULO_AVISO: Record<string, string> = {
  SIMPLES_CBS_IBS_A_CONFIRMAR: 'partilha de CBS/IBS do Simples a confirmar — usando a alíquota efetiva digitada sem CBS/IBS',
};

const ROTULO_ESCOLHA: Record<SimplesResumoResponse['escolha'], string> = { I: 'Anexo I', II: 'Anexo II', III: 'Anexo III', V: 'Anexo V', FATOR_R: 'Fator R' };
const ROTULO_DAS_ORIGEM: Record<SimplesResumoResponse['dasOrigem'], string> = { CALCULADA: 'calculado', DIGITADA: 'digitado, a confirmar' };
const CONTA_SIMPLES = '(RBT12 × nominal − parcela a deduzir) ÷ RBT12';

/** Rotulo da incidencia (texto fixo; o valor vem da API). */
const ROTULO_INCIDENCIA: Record<TributosPorOrigemResponse['linhas'][number]['incidencia'], string> = {
  DENTRO: 'dentro do preço',
  FORA: 'por fora',
  LUCRO: 'sobre o lucro',
};

/** Une os avisos do perfil e da formacao sem repetir o mesmo aviso (codigo, tributo e texto iguais). */
export function mesclarAvisos(...listas: Array<readonly AvisoTributario[] | undefined>): AvisoTributario[] {
  const vistos = new Set<string>();
  return listas.flatMap((l) => l ?? []).filter((a) => {
    const chave = `${a.codigo}|${a.tributo ?? ''}|${a.mensagem}`;
    if (vistos.has(chave)) return false;
    vistos.add(chave);
    return true;
  });
}

function Cartao({ titulo, children, testid }: { titulo: string; children: ReactNode; testid: string }) {
  return (
    <div role="alert" data-testid={testid} className="flex flex-[1_1_320px] items-start gap-3 rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-3.5">
      <AlertTriangle aria-hidden="true" className="mt-px size-[18px] shrink-0 text-warning-ink" />
      <div className="min-w-0">
        <div className="font-semibold">{titulo}</div>
        <div className="text-small text-ink-secondary">{children}</div>
      </div>
    </div>
  );
}

/** Avisos de substituicao e de tributo nao configurado, no topo do resultado. Outros avisos ficam no detalhe de tributos. */
export function AvisosTributarios({ avisos }: { avisos: AvisoTributario[] }) {
  const substituicao = avisos.filter((a) => CODIGOS_SUBSTITUICAO.includes(a.codigo));
  const naoConfigurado = avisos.filter((a) => CODIGOS_NAO_CONFIGURADO.includes(a.codigo));
  if (substituicao.length === 0 && naoConfigurado.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-3" data-testid="avisos-tributarios">
      {substituicao.length > 0 && (
        <Cartao titulo="Substituição tributária" testid="aviso-substituicao">
          {substituicao.map((a) => (
            <p key={`${a.codigo}-${a.tributo ?? ''}`}>{a.mensagem}</p>
          ))}
        </Cartao>
      )}
      {naoConfigurado.length > 0 && (
        <Cartao titulo="Tributo não configurado" testid="aviso-nao-configurado">
          {naoConfigurado.map((a) => (
            <p key={`${a.codigo}-${a.tributo ?? ''}`}>{a.mensagem}</p>
          ))}
        </Cartao>
      )}
    </div>
  );
}

/**
 * Botao (i) que abre o detalhe dos tributos por origem: linha por linha com o texto da API, a incidencia, o selo "informativa"
 * e o R$ da base escolhida. O total soma so os valores que a API mandou (nulos, como os de tributo sobre o lucro, ficam de fora).
 */
export function DetalheTributosOrigem({ tributos, base = 'sugerido', avisos }: { tributos: TributosPorOrigemResponse; base?: BaseValoresDespesa; avisos: AvisoTributario[] }) {
  const [aberto, setAberto] = useState(false);
  const idConteudo = useId();
  const ancora = useRef<HTMLButtonElement>(null);
  const valorDaLinha = (l: TributosPorOrigemResponse['linhas'][number]) => (base === 'praticado' ? l.valorPraticado : l.valorSugerido);
  const total = somar(tributos.linhas.map(valorDaLinha));
  const outros = avisos.filter((a) => !CODIGOS_SUBSTITUICAO.includes(a.codigo) && !CODIGOS_NAO_CONFIGURADO.includes(a.codigo));

  return (
    <PopoverPrimitive.Root open={aberto} onOpenChange={setAberto}>
      <PopoverPrimitive.Anchor asChild>
        <button
          ref={ancora}
          type="button"
          aria-label="Ver tributos por origem"
          aria-expanded={aberto}
          aria-controls={idConteudo}
          onClick={() => setAberto(true)}
          onFocus={() => setAberto(true)}
          className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-ink-muted outline-none hover:text-ink-body focus-visible:shadow-focus"
        >
          <Info aria-hidden="true" className="size-3.5" />
        </button>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          id={idConteudo}
          role="dialog"
          aria-label="Tributos por origem"
          align="end"
          sideOffset={6}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => {
            if (ancora.current?.contains(e.target as Node)) e.preventDefault();
          }}
          className="z-50 w-[22rem] max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-popover p-3 text-small shadow-menu"
        >
          <ul className="flex flex-col gap-2.5">
            {tributos.linhas.map((l, i) => (
              <li key={`${l.tributo}-${l.origem}-${i}`} className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="rounded-xs bg-line-soft px-1.5 py-0.5 text-caption font-medium">{l.tributo}</span>
                    <span className="text-caption text-ink-muted">{ROTULO_INCIDENCIA[l.incidencia]}</span>
                    {l.informativa && <span className="rounded-xs border border-border px-1.5 py-0.5 text-caption text-ink-secondary">informativa</span>}
                  </div>
                  <p className="mt-0.5 break-words text-ink-body">{l.texto}</p>
                </div>
                <span className="tabular whitespace-nowrap text-right">{formatarDinheiro(valorDaLinha(l))}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-t border-border pt-2 font-semibold">
            <span>Total</span>
            <span className="tabular whitespace-nowrap text-right">{formatarDinheiro(total)}</span>
          </div>
          {tributos.simples && <BlocoSimples simples={tributos.simples} />}
          {outros.length > 0 && (
            <div className="mt-3 flex flex-col gap-1.5 border-t border-border pt-2 text-caption text-ink-secondary">
              {outros.map((a, i) => (
                <p key={`${a.codigo}-${i}`}>{ROTULO_AVISO[a.codigo] ?? a.mensagem}</p>
              ))}
            </div>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** Sessao 52: Simples com efetiva CALCULADA. Valores prontos da API (fracoes e decimais como texto); a tela so formata e mostra a conta. */
export function BlocoSimples({ simples: s }: { simples: SimplesResumoResponse }) {
  return (
    <section aria-label="Simples calculado" data-testid="simples-calculado" className="mt-3 flex flex-col gap-1.5 border-t border-border pt-2 text-small">
      <div className="font-semibold">Simples calculado</div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-ink-secondary">
        <dt>Anexo</dt>
        <dd className="tabular">{s.anexo}</dd>
        <dt>Configurado</dt>
        <dd>{ROTULO_ESCOLHA[s.escolha]}</dd>
        <dt>Faixa</dt>
        <dd className="tabular">{s.faixa}</dd>
        <dt>Alíquota nominal</dt>
        <dd className="tabular">{formatarFracaoPercentual(s.aliquotaNominal)}</dd>
        <dt>Parcela a deduzir (R$)</dt>
        <dd className="tabular">{formatarDinheiro(s.parcelaDeduzir)}</dd>
        <dt>RBT12 (R$)</dt>
        <dd className="tabular">{formatarDinheiro(s.rbt12)}</dd>
        {s.fatorR !== null && (
          <>
            <dt>Fator R</dt>
            <dd className="tabular">{formatarFracaoPercentual(s.fatorR)}</dd>
          </>
        )}
        <dt>Efetiva</dt>
        <dd className="tabular">{formatarFracaoPercentual(s.efetiva)}</dd>
        <dt>DAS no preço</dt>
        <dd className="tabular">
          {formatarFracaoPercentual(s.dasNoPreco)} <span className="text-caption text-ink-muted">({ROTULO_DAS_ORIGEM[s.dasOrigem]})</span>
        </dd>
      </dl>
      <p className="text-caption text-ink-muted">{CONTA_SIMPLES}</p>
      <a href={s.tabelaLegal.fonteUrl} target="_blank" rel="noreferrer" className="text-caption text-primary underline-offset-2 hover:underline">
        Fonte da tabela legal (vigente desde {formatarData(s.tabelaLegal.vigenciaInicio)})
      </a>
    </section>
  );
}

/** Regime REAL: lucro antes e depois do IR no preco sugerido (fracoes da API). So aparece quando a API mandou `lucro`. */
export function BlocoLucroReal({ lucro, metaLucro }: { lucro: NonNullable<TributosPorOrigemResponse['lucro']>; metaLucro: TributosPorOrigemResponse['metaLucro'] }) {
  return (
    <div data-testid="lucro-real" className="flex flex-col gap-1.5 border-t border-dashed border-border px-5 py-3.5 text-small">
      <div className="font-medium text-ink-body">Lucro no Real · meta {metaLucro === 'LIQUIDA_IR' ? 'líquida de IR' : 'antes do IR'}</div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-ink-secondary">
        <span>
          Antes do IR <b className="tabular text-ink">{formatarFracaoPercentual(lucro.antesIr)}</b>
        </span>
        <span>
          IR sobre o lucro <b className="tabular text-ink">{formatarFracaoPercentual(lucro.irSobreLucro)}</b>
        </span>
        <span>
          Depois do IR <b className="tabular text-ink">{formatarFracaoPercentual(lucro.liquido)}</b>
        </span>
      </div>
    </div>
  );
}
